import React, { Fragment, FunctionComponent, MouseEvent, useCallback } from "react";

import { AnswerTemplate } from "../../db/models/Answer";
import { FormInterstitial, QuestionGroup as FormQuestionGroup } from "../../db/models/Form";
import Question from "../../db/models/Question";
import { useTranslate } from "../translation/frontend";
import { PaginationWide } from "./Pagination";
import { QuestionTitle } from "./Question";
import ArrowRightIcon from "./icons/ArrowRightIcon";
import CheckIcon from "./icons/CheckIcon";
import { shouldSkipPage } from "./page-logic";

interface ReviewInterstitialProps {
  page: FormInterstitial;
  onBack?: (ev: MouseEvent<HTMLButtonElement>) => void;
  onSkipToEnd?: (ev: MouseEvent<HTMLButtonElement>) => void;
  onChangePage: (pageNumber: number, focusQuestion?: string) => void;
  isLastPage: boolean;
  loading: boolean;
  showValidationError: boolean;
  pages: (FormQuestionGroup | FormInterstitial)[];
  answers: Record<string, AnswerTemplate[]>;
}

const GroupedAnswers: FunctionComponent<{ question: Question; answers: AnswerTemplate[] }> = ({
  question,
  answers,
}) => {
  const answersToOptionGroup: Record<string, string> = {};
  for (const answer of answers) {
    const optionGroupId = question.optionGroups?.find(({ options }) =>
      options.some(({ id }) => id === answer.optionId),
    )?.id;
    if (!optionGroupId) continue;
    answersToOptionGroup[answer.optionId!] = optionGroupId;
  }

  const usedOptionGroups = new Set<string>(Object.values(answersToOptionGroup));

  return (
    <>
      {question
        .optionGroups!.filter(({ id }) => usedOptionGroups.has(id))
        .map(({ groupName, id }) => (
          <Fragment key={id}>
            <p className="report-review-answer-group">{groupName}</p>
            <ul className="report-review-answers">
              {answers
                .filter((answer) => answersToOptionGroup[answer.optionId!] === id)
                .map((answer, i) => (
                  <li className="report-review-answer" key={answer.optionId!}>
                    <ReviewAnswer answer={answer} />
                  </li>
                ))}
            </ul>
          </Fragment>
        ))}
    </>
  );
};

const ReviewAnswer: FunctionComponent<{ answer: AnswerTemplate }> = ({ answer }) => {
  const translate = useTranslate();

  return answer.optionId ? (
    <>
      {answer.optionText}
      {answer.freeText ? `: ${answer.freeText}` : ""}
    </>
  ) : !answer.freeText ? (
    <em>{translate("questionNotAnswered")}</em>
  ) : (
    <>{answer.freeText}</>
  );
};

const ChangeButton: FunctionComponent<{
  questionId: string;
  pageNumber: number;
  onChange: (pageNumber: number, focusQuestion?: string) => void;
}> = ({ pageNumber, questionId, onChange }) => {
  const translate = useTranslate();
  const handleClick = useCallback(() => {
    onChange(pageNumber, questionId);
  }, [onChange, pageNumber, questionId]);

  return (
    <button
      className="report-review-change"
      type="submit"
      name="_change"
      value={pageNumber.toString()}
      onClick={handleClick}
    >
      {translate("change")}
    </button>
  );
};

const ReviewInterstitial: FunctionComponent<ReviewInterstitialProps> = ({
  page,
  onBack,
  onSkipToEnd,
  onChangePage,
  isLastPage,
  loading,
  showValidationError,
  pages,
  answers,
}) => {
  const translate = useTranslate();

  return (
    <>
      <section className="form-group-intro section">
        <div className="container--slim">
          <div className="report-review-intro">
            <div className="content-styles allow-cross-form-links" dangerouslySetInnerHTML={{ __html: page.body }} />
          </div>
        </div>
      </section>
      {page.showSkipToEnd ? (
        <section className="form-group-intro form-group-intro__skip-to-end section">
          <div className="container--slim container--slim__skip-to-end">
            <button
              className="btn btn--skip-to-end btn--swipe btn--arrow"
              name="_action"
              value="skip-to-end"
              onClick={onSkipToEnd}
              formNoValidate
              disabled={loading}
            >
              <span>{translate("skipToEndLabel")}</span>
              <span className="btn__arrow">
                <ArrowRightIcon />
              </span>
            </button>
          </div>
        </section>
      ) : null}

      <section className="form-group section">
        <div className="container--slim">
          <h2>{translate("reviewAnswers")}</h2>
          {pages.map((page, i) =>
            page.type !== "QUESTION_GROUP" || shouldSkipPage(page, answers) ? null : (
              <Fragment key={i}>
                {page.questions.map((question) => {
                  const questionAnswers = answers[question.id] ?? [];
                  return (
                    <Fragment key={question.id}>
                      <h3 className="report-review-question">
                        <QuestionTitle question={question} answers={answers} />
                      </h3>
                      {question.questionType === "IMAGE" ? (
                        <p className="report-review-answer">
                          {translate("imagesAttached", { count: questionAnswers.length })}{" "}
                          <ChangeButton questionId={question.id} pageNumber={i + 1} onChange={onChangePage} />
                        </p>
                      ) : questionAnswers.length === 0 ? (
                        <p className="report-review-answer">
                          <em>{translate("questionNotAnswered")}</em>{" "}
                          <ChangeButton questionId={question.id} pageNumber={i + 1} onChange={onChangePage} />
                        </p>
                      ) : questionAnswers.length === 1 &&
                        (!question.optionGroups || question.optionGroups.length === 1) ? (
                        <p className="report-review-answer">
                          <ReviewAnswer answer={questionAnswers[0]} />{" "}
                          <ChangeButton questionId={question.id} pageNumber={i + 1} onChange={onChangePage} />
                        </p>
                      ) : (
                        <>
                          <GroupedAnswers question={question} answers={questionAnswers} />
                          <p>
                            <ChangeButton questionId={question.id} pageNumber={i + 1} onChange={onChangePage} />
                          </p>
                        </>
                      )}
                    </Fragment>
                  );
                })}
              </Fragment>
            ),
          )}
        </div>
      </section>

      {page.confirm ? (
        <section className="form-group section">
          <div className="container--slim">
            <div className="field field--isRequired field--isSingle">
              <div className="options__option options__option--interstitial">
                <div className="options__label">
                  <input
                    type="checkbox"
                    className="form-control options__check options__checkbox"
                    id="confirm"
                    required
                  />
                  <CheckIcon />
                  <div className="options__label-target">
                    <label htmlFor="confirm">{page.confirm}</label>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      ) : null}

      <PaginationWide
        loading={loading}
        showValidationError={showValidationError}
        isLastPage={isLastPage}
        onBack={onBack}
      />
    </>
  );
};

export default ReviewInterstitial;
